// src/pages/organization/subscription/packages.js
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../../contexts/PageContext";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import ErrorMessage from "../../../components/common/ErrorMessage";
import PackageCard from "../../../components/organization/subscription/PackageCard";
import PendingPaymentCard from "../../../components/organization/subscription/PendingPaymentCard";
import useSubscriptionPackages from "../../../hooks/useSubscriptionPackages";
import useSubscriptionStatus from "../../../hooks/useSubscriptionStatus";
import usePendingPayment from "../../../hooks/usePendingPayment";
import SubscriptionUpgrade from "./upgrade";

const SubscriptionPackages = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const { packages, loading: packagesLoading, error: packagesError, refetch: refetchPackages } = useSubscriptionPackages();
  const { hasActiveSubscription, loading: statusLoading, error: statusError, refetch: refetchStatus } = useSubscriptionStatus();
  const { pendingPayment, loading: pendingLoading, error: pendingError, refetch: refetchPending } = usePendingPayment();

  useEffect(() => {
    updatePageTitle(t("subscription.title"), t("subscription.subtitle"));
  }, [updatePageTitle, t]);

  // If checking subscription status or pending payment, show loading
  if (statusLoading || pendingLoading) {
    return <LoadingSpinner message={t("subscription.checkingStatus")} />;
  }

  // If there's an error checking status, show error
  if (statusError || pendingError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <ErrorMessage message={statusError || pendingError} />
        <div className="text-center mt-4">
          <button 
            onClick={() => {
              refetchStatus();
              refetchPending();
            }}
            className="px-6 py-2 text-white rounded-lg transition-colors shadow-lg"
            style={{ backgroundColor: '#DE561F' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#C54E1C'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#DE561F'}
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  // If user has pending payment, show pending payment details
  if (pendingPayment) {
    const handleRetryPayment = async (packageId) => {
      try {
        console.log(`🔄 Creating new payment link for package: ${packageId}`);

        // Call backend payment endpoint to create new payment link
        const response = await fetch(`https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/organization/subscription/pay`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            packageId: packageId
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create new payment link');
        }

        if (data.success && data.data.type === 'geidea' && data.data.paymentLink) {
          // Open new payment link
          window.open(data.data.paymentLink, '_blank');
          
          // Refresh pending payment data
          refetchPending();
          
          alert(`💳 New payment link created and opened in new tab.\n\nPlease complete the payment and return to this page.`);
        } else {
          throw new Error('Failed to create payment link');
        }

      } catch (error) {
        console.error('💥 Retry payment error:', error);
        alert(`❌ Error creating new payment link\n\n${error.message}\n\nPlease try again or contact support.`);
      }
    };

    return (
      <div className="">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t("subscription.completePayment")}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("subscription.pendingPaymentMessage")}
            </p>
          </div>

          {/* Pending Payment Card */}
          <PendingPaymentCard 
            pendingPayment={pendingPayment}
            onRetryPayment={handleRetryPayment}
          />
        </div>
      </div>
    );
  }

  // If user has active subscription, show upgrade page
  if (hasActiveSubscription) {
    return <SubscriptionUpgrade />;
  }

  // Otherwise, show regular packages page for new subscriptions

  const handleSelectPackage = (selectedPackage) => {
    // TODO: Implement package selection logic
    console.log('Selected package:', selectedPackage);
    // This could navigate to a payment page or open a modal
  };

  if (packagesLoading) {
    return <LoadingSpinner message={t("subscription.loadingPackages")} />;
  }

  if (packagesError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <ErrorMessage message={packagesError} />
        <div className="text-center mt-4">
          <button 
            onClick={refetchPackages}
            className="px-6 py-2 text-white rounded-lg transition-colors shadow-lg"
            style={{ backgroundColor: '#DE561F' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#C54E1C'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#DE561F'}
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t("subscription.choosePerfectPlan")}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t("subscription.selectPackageMessage")}
          </p>
        </div>

        {/* Packages Grid */}
        {packages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {packages.map((pkg, index) => (
              <PackageCard 
                key={pkg.id} 
                pkg={pkg} 
                isPopular={index === 1} // Mark middle package as popular
                onSelectPackage={handleSelectPackage}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">{t("subscription.noPackagesAvailable")}</h3>
            <p className="text-gray-600">{t("subscription.noPackagesMessage")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPackages;